import {
  Box,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Header,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { ACCESS_PROVIDER_GALAXI } from 'src/commons/constants';
import { dataSetDetail } from 'src/api/catalog';
import { DataBadges } from 'src/components/catalog/common';
import { GetDataSetDetailResponse } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';

export interface DetailsPageHeaderProps {
  header: any;
  buttons: any;
  notifyFailure: any;
  cti?: string;
  dataset?: any;
  tags?: any;
  businessGlossaries?: any;
  governanceTags?: any;
  hasLineage?: boolean;
  hasDataQuality?: boolean;
  selectedVersionOption: any;
  setSelectedVersionOption: any;
  setMostRecentVersionOption: any;
}

export const getFormattedOptions = (listOfDataSetDetailResponses) => {
  const mappedList = listOfDataSetDetailResponses.map((i) => ({
    id: i.VersionId.toString(),
    label: 'Version ' + i.VersionId,
    item: i,
  }));
  mappedList.sort(function (a, b) {
    return b.item.VersionId - a.item.VersionId;
  });
  return mappedList;
};

export const DetailsPageHeader = (props: DetailsPageHeaderProps) => {
  const {
    header,
    buttons,
    notifyFailure,
    cti,
    dataset,
    selectedVersionOption,
    setSelectedVersionOption,
    setMostRecentVersionOption,
  } = props;

  const [versionOptions, setVersionOptions] = useState<any[]>([]);
  const [nextToken, setNextToken] = useState<string>(undefined);

  const datasetId = dataset?.Id;

  useEffect(() => {
    fetchDetails();
  }, []);

  const fetchDetails = async () => {
    let getDataSetDetailResponse: GetDataSetDetailResponse;
    try {
      getDataSetDetailResponse = await dataSetDetail({
        Filter: {
          IdList: [datasetId],
        },
      });
    } catch (err) {
      notifyFailure('Failed to load dataset details: ' + err.message);
    }
    // we reverse the version list so that the newest versions show first
    const formattedOptions = getFormattedOptions(getDataSetDetailResponse.DataSetDetailList);
    const noError = formattedOptions.length > 0;
    if (noError) {
      formattedOptions[0].label = formattedOptions[0].label + ' (latest)';
    }
    if (getDataSetDetailResponse.NextToken != null) {
      formattedOptions.push({
        id: 'load-more-datasets',
        label: 'Load more...',
        item: null,
      });
    }
    if (!noError) {
      notifyFailure('Failed to load metadata version list.');
    }

    const newOption = noError ? formattedOptions[0] : null;

    setVersionOptions(formattedOptions);
    setMostRecentVersionOption(newOption);
    setSelectedVersionOption(undefined);
    setNextToken(getDataSetDetailResponse.NextToken);
  };

  const handleSelectChange = async (e) => {
    const selectedOption = e.detail.selectedOption;
    if (selectedOption != undefined && selectedOption.id == 'load-more-datasets') {
      const combinedOptions = [];
      for (let i = 0; i < versionOptions.length - 1; i++) {
        combinedOptions.push(versionOptions[i]);
      }

      const response = await dataSetDetail({
        NextToken: nextToken,
      });

      combinedOptions.push(...getFormattedOptions(response.DataSetDetailList));

      if (response.NextToken != null) {
        combinedOptions.push(versionOptions[versionOptions.length - 1]);
      }

      // Note that option should remain unchanged
      setVersionOptions(combinedOptions);
      setNextToken(response.NextToken);
      setSelectedVersionOption(combinedOptions[versionOptions.length - 1]);
    } else {
      setSelectedVersionOption(e.detail.selectedOption);
    }
  };

  return (
    <SpaceBetween direction='vertical' size='xs'>
      <Header
        variant={'h1'}
        actions={
          <SpaceBetween size={'s'} direction='horizontal'>
            {ACCESS_PROVIDER_GALAXI != dataset?.AccessProvider && (
              <Select
                ariaLabel={'version1'}
                options={versionOptions}
                selectedOption={selectedVersionOption}
                loadingText='Loading...'
                placeholder='Metadata history'
                errorText='Error: Unable to load metadata history.'
                ariaRequired={true}
                onChange={handleSelectChange}
              />
            )}
            {buttons.map((button, key) =>
              !button.items ? (
                <Button
                  href={button.href || ''}
                  disabled={button.disabled || false}
                  iconName={button.icon}
                  onClick={button.onItemClick}
                  key={key}
                  loading={button.loading || false}
                  variant={button.variant || 'normal'}
                >
                  {button.text}
                </Button>
              ) : (
                <ButtonDropdown
                  items={button.items}
                  key={key}
                  onItemClick={button.onItemClick}
                  loading={button.loading}
                >
                  {button.text}
                </ButtonDropdown>
              ),
            )}
          </SpaceBetween>
        }
      >
        {header}
      </Header>

      <ColumnLayout columns={2}>
        <DataBadges
          tags={props.tags}
          dataset={props.dataset}
          businessGlossaries={props.businessGlossaries}
          governanceTags={props.governanceTags}
          hasLineage={props.hasLineage}
          hasDataQuality={props.hasDataQuality}
        />
        {!!cti && (
          <Box textAlign={'right'} float={'right'}>
            <b>Owner CTI: </b>
            {cti}
          </Box>
        )}
      </ColumnLayout>
    </SpaceBetween>
  );
};
